/**
 * DescriptionStep
 *
 * Third step of the add content flow.
 * Text input for description with #hashtag suggestions and @mention support.
 * Matches Flutter's SetContentDescriptionScreen from
 * set_content_description_screen.dart.
 */

import React, { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useRegisterStepNav } from '@/presentation/components/common/stepper';
import { useContentDescription, type DescriptionResult } from '../../hooks/useContentDescription';
import { DescriptionInput } from './descriptionStep/DescriptionInput';
import { HashtagSuggestionsList } from './descriptionStep/HashtagSuggestionsList';

interface DescriptionStepProps {
  onNext: (result: DescriptionResult) => void;
  onBack: () => void;
  screenKey?: string;
}

export const DescriptionStep: React.FC<DescriptionStepProps> = ({
  onNext,
  onBack,
  screenKey,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const {
    description,
    inputRef,
    isEditingHashTags,
    hashtagSuggestions,
    isFetchingHashTags,
    isEditingMentions,
    handleDescriptionChange,
    selectHashtag,
    insertHashtagSymbol,
    insertMentionSymbol,
    handleFocusChange,
    loadMoreHashTags,
    processAndFinish,
    setValueAndCursor,
  } = useContentDescription({ screenKey });

  const handleNext = useCallback(() => {
    const result = processAndFinish();
    onNext(result);
  }, [processAndFinish, onNext]);

  useRegisterStepNav(
    { onNext: handleNext, onPrevious: onBack },
    [handleNext, onBack],
  );

  return (
    <div
      className="scrollbar-hide"
      style={{
        height: '100%',
        overflowY: 'auto',
        padding: `0 ${responsive.spacing.xl}px`,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
        }}
      >
        {/* Description input — sticky at top */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: theme.modalBackground,
            paddingTop: responsive.spacing.xl,
            paddingBottom: responsive.spacing.lg,
          }}
        >
          <DescriptionInput
            value={description}
            inputRef={inputRef}
            isEditingMentions={isEditingMentions}
            onChange={handleDescriptionChange}
            onFocusChange={handleFocusChange}
            onInsertHashtag={insertHashtagSymbol}
            onInsertMention={insertMentionSymbol}
            setValueAndCursor={setValueAndCursor}
          />
        </div>

        {/* Hashtag suggestions list */}
        {isEditingHashTags && (
          <HashtagSuggestionsList
            suggestions={hashtagSuggestions}
            isFetching={isFetchingHashTags}
            onSelect={selectHashtag}
            onLoadMore={loadMoreHashTags}
          />
        )}

      </div>
    </div>
  );
};
